import { asApiFailure, parseJsonObject } from "@utils/apiFailure";
import { PASSWORD_REFUSED_MESSAGE } from "./passwordPolicy";

// The complete-signup POST fails in four shapes the landing page must tell
// apart, because each drives a different UI transition:
//   - 400 with an error under the password's key → the password was refused;
//          say so on the password field (#336). The password is the only field
//          in the body, and the API answers both of its refusals under that
//          key: Entra refusing a banned or common one, and the API's own rule
//          (reachable from this page only if its rule drifted from the API's).
//          The API's first message is shown; without a usable one, this page's
//          own copy. A 400 with no password error falls through to generic.
//   - 409  the email is already registered → contact us (OB-03), or sign in
//          if the account is their own
//   - 404/410  the token is now invalid/expired/consumed → show the invalid state
//   - anything else → a generic inline retry message
export type CompleteInvitationFailure =
    | { kind: "password-refused"; message: string }
    | { kind: "already-registered" }
    | { kind: "invalid-token" }
    | { kind: "generic" };

// The key ValidationProblemDetails reports the password under, as the API's
// validator names it (the command's Body.Password).
const PASSWORD_ERROR_KEY = "Body.Password";

// The message to show for a 400's password error, or undefined when the body
// holds none.
const passwordRefusal = (detail: string | undefined): string | undefined => {
    const errors = parseJsonObject(detail)?.errors;

    if (
        typeof errors !== "object" ||
        errors === null ||
        Array.isArray(errors) ||
        !(PASSWORD_ERROR_KEY in errors)
    ) {
        return undefined;
    }

    const messages = (errors as Record<string, unknown>)[PASSWORD_ERROR_KEY];
    const first: unknown = Array.isArray(messages) ? messages[0] : undefined;

    return typeof first === "string" && first.trim()
        ? first
        : PASSWORD_REFUSED_MESSAGE;
};

// The status is read through the shared asApiFailure, which duck-types the
// ApiError useApi throws, so this helper stays free of the useApi module's
// config/MSAL imports and is trivially unit-testable.
export const completeInvitationOutcome = (
    error: unknown,
): CompleteInvitationFailure => {
    const failure = asApiFailure(error);

    switch (failure?.status) {
        case 400: {
            const message = passwordRefusal(failure.detail);

            return message === undefined
                ? { kind: "generic" }
                : { kind: "password-refused", message };
        }

        case 409:
            return { kind: "already-registered" };
        case 404:
        case 410:
            return { kind: "invalid-token" };
        default:
            return { kind: "generic" };
    }
};
